import { useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import OTPInput from 'react-otp-input';
import { Link } from 'react-router-dom';
import CkycDetailsCard from '../../LoanApplications/CKYC/CkycDetailsCard';
import CkycCard from '../../LoanApplications/CKYC/ckycCard';
import { extractErrorMessage } from '../../LoanApplications/CKYC/ckycHelper';

const CKYC = (props) => {
    const formRef = useRef(null);
    const tabStatus = props.urlparams.params.status;
    const frompath = props?.urlparams?.params?.frompath;
    const productTypeParam = props.urlparams.params.producttype;
    const isAdmin = props?.$store?.persona === _enum.ROUTE_PERSONAS.admin;
    const [customerId, setCustomerId] = useState(null);
    const [selectedIdentifier, setSelectedIdentifier] = useState(_enum.IDENTIFIER_TYPE.find(item => item.key === "PAN") || _enum.IDENTIFIER_TYPE[0]);
    const [validationError, setValidationError] = useState(null);
    const [ckycData, setCkycData] = useState(null);
    const [customerCkyc, setCustomerCkyc] = useState(null);
    const [showCkycModal, setShowCkycModal] = useState(false);
    const [showCkycOtpInput, setShowCkycOtpInput] = useState(false);
    const [ckycOtpValue, setCkycOtpValue] = useState("");
    const [ckycDetails, setCkycDetails] = useState(null);
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [resendTimer, setResendTimer] = useState(30);
    const [isResending, setIsResending] = useState(false);

    // Timer effect for Resend OTP
    useEffect(() => {
        let timer;
        if (showCkycOtpInput && resendTimer > 0) {
            timer = setInterval(() => {
                setResendTimer((prev) => prev - 1);
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [showCkycOtpInput, resendTimer]);

    const closeCkycModal = () => {
        setShowCkycModal(false);
        setShowCkycOtpInput(false);
        setCkycOtpValue("");
        setResendTimer(30);
        setIsResending(false);
        if (isRegenerating && !ckycDetails) {
            setIsRegenerating(false);
        }
    };

    const handleResendOtp = () => {
        if (resendTimer > 0 || isResending) return;
        setIsResending(true);
        const payload = {
            customerId: customerId,
        };
        props.apis.apiUnderWriterCkycResendOtp(payload)
            .then((res) => {
                setIsResending(false);
                if (res?.http_response_code === 200 || res?.result_code === 103 || res?.status === "SUCCESS") {
                    setResendTimer(30);
                    swal2.fire({
                        title: "OTP Resent Successfully",
                        text: res?.message || "A new OTP has been sent to the registered mobile number.",
                        icon: "success",
                        timer: 2000,
                        showConfirmButton: false,
                    });
                } else {
                    const errorMsg = extractErrorMessage(res, "Failed to resend OTP");
                    swal2.fire({
                        title: errorMsg,
                        icon: "error",
                        iconColor: "#FF4D4D",
                    });
                }
            })
            .catch((err) => {
                setIsResending(false);
                const errorMsg = extractErrorMessage(err, "Failed to resend OTP");
                swal2.fire({
                    title: errorMsg,
                    icon: "error",
                    iconColor: "#FF4D4D",
                });
            });
    };

    const handleCkycOtpVerify = () => {
        if (isRegenerating && !isAdmin) {
            swal2.fire({
                title: "Unauthorized Action",
                text: "Only Admin users have permission to regenerate CKYC reports.",
                icon: "error",
                iconColor: "#FF4D4D",
            });
            return;
        }

        if (!ckycOtpValue || ckycOtpValue.length < 6) {
            swal2.fire({
                title: "Please enter a valid OTP",
                icon: "error",
                iconColor: "#FF4D4D",
            });
            return;
        }

        const payload = {
            customerId: customerId,
            otp: ckycOtpValue,
            ...(isRegenerating ? { isRegenerate: true } : {}),
        };

        props.apis.apiUnderWriterSubmitCkycOtp(payload)
            .then((res) => {
                if (res) {
                    if (
                        res?.status === "INVALID" ||
                        res?.status === "FAILED" ||
                        res?.status === "FAILURE" ||
                        (res?.result_code && res?.result_code !== 101) ||
                        (!res?.result && (res?.error || res?.message))
                    ) {
                        const errorMsg = extractErrorMessage(res, "Invalid OTP entered");
                        swal2.fire({
                            title: errorMsg,
                            icon: "error",
                            iconColor: "#FF4D4D",
                        });
                        return;
                    }

                    swal2.fire({
                        title: "CKYC Generated Successfully",
                        text: "Please review the report and click 'Save & Submit' to finalize.",
                        icon: "success",
                    });
                    if (res.result) {
                        setCkycDetails(res.result);
                    }
                    closeCkycModal();
                }
            })
            .catch((err) => {
                const errorMsg = extractErrorMessage(err, "Failed to verify OTP");
                swal2.fire({
                    title: errorMsg,
                    icon: "error",
                    iconColor: "#FF4D4D",
                });
            });
    };

    const handleDownloadReport = () => {
        if (!customerId) {
            swal2.fire({
                title: "No customer ID available for download",
                icon: "warning",
            });
            return;
        }

        props.apis.apiUnderWriterDownloadCkycPdf({ customerId })
            .then((res) => {
                if (res) {
                    const blob = res instanceof Blob ? res : new Blob([res], { type: 'application/pdf' });
                    if (blob.size === 0) {
                        swal2.fire({
                            title: "Failed to download CKYC report",
                            text: "Received empty report file from server.",
                            icon: "error",
                            iconColor: "#FF4D4D",
                        });
                        return;
                    }
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `CKYC_Report_${customerId}.pdf`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                } else {
                    swal2.fire({
                        title: "Failed to download CKYC report",
                        icon: "error",
                        iconColor: "#FF4D4D",
                    });
                }
            })
            .catch(async (err) => {
                let errorData = err?.response?.data;
                if (errorData instanceof Blob) {
                    try {
                        const text = await errorData.text();
                        errorData = JSON.parse(text);
                    } catch (e) {
                        // ignore parse error
                    }
                }
                const errorMsg = extractErrorMessage(errorData || err, "Failed to download CKYC report");
                swal2.fire({
                    title: errorMsg,
                    icon: "error",
                    iconColor: "#FF4D4D",
                });
            });
    };

    const handleRegenerateCKYC = () => {
        if (!isAdmin) {
            swal2.fire({
                title: "Unauthorized Action",
                text: "Only Admin users have permission to regenerate CKYC reports.",
                icon: "error",
                iconColor: "#FF4D4D",
            });
            return;
        }

        swal2.fire({
            title: "Regenerate CKYC Report",
            text: "Are you sure you want to regenerate the CKYC report? This will allow you to generate a fresh report and update the stored data.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, Regenerate",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#bb2124",
            cancelButtonColor: "#fff",
        }).then((result) => {
            if (result.isConfirmed) {
                setIsRegenerating(true);
                setCkycDetails(null);
                setShowCkycModal(true);
                setShowCkycOtpInput(false);
                setCkycOtpValue("");
                setResendTimer(30);
            }
        });
    };

    const handleCancelRegenerate = () => {
        setIsRegenerating(false);
        setCkycDetails(null);
    };

    const handleSaveAndSubmit = () => {
        if (!ckycDetails) return;

        swal2.fire({
            title: "Save & Submit CKYC Report",
            text: "Are you sure you want to save and submit the CKYC report?",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Save & Submit",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#bb2124",
            cancelButtonColor: "#6c757d",
        }).then((result) => {
            if (result.isConfirmed) {
                const payload = {
                    customerId: customerId,
                    result: ckycDetails,
                };

                props.apis.apiUnderWriterCkycSaveDownload(payload)
                    .then((res) => {
                        setIsRegenerating(false);
                        setCkycDetails(null);
                        getUnderwriterInfo();
                        swal2.fire({
                            title: "CKYC Report Saved & Submitted Successfully",
                            icon: "success",
                        }).then(() => {
                            props.changeTab?.(null, "backgroundverification");
                        });
                    })
                    .catch((err) => {
                        const errorMsg = extractErrorMessage(err, "Failed to save CKYC report");
                        swal2.fire({
                            title: errorMsg,
                            icon: "error",
                            iconColor: "#FF4D4D",
                        });
                    });
            }
        });
    };

    const handleChange = (value) => {
        let val = value[0]?.key || "";
        const selected = _enum.IDENTIFIER_TYPE.find((item) => item.key === val) || _enum.IDENTIFIER_TYPE[0];
        setSelectedIdentifier(selected);
        setValidationError(null);
        console.log({ val });
    };

    const handleSubmit = () => {

        const data = formRef.current?.form?.current?.getData?.() || {};
        const identifierVal = data.identifier || "";

        if (!identifierVal || identifierVal.trim() === "") {
            setValidationError("This field is required");
            return;
        }

        if (selectedIdentifier) {
            if (selectedIdentifier.pattern && !new RegExp(selectedIdentifier.pattern).test(identifierVal)) {
                setValidationError(selectedIdentifier.invalidmessage || "Invalid format");
                return;
            }
            if (selectedIdentifier.minLength && identifierVal.length < selectedIdentifier.minLength) {
                setValidationError(selectedIdentifier.invalidmessage || "Invalid format");
                return;
            }
            if (selectedIdentifier.maxLength && identifierVal.length > selectedIdentifier.maxLength) {
                setValidationError(selectedIdentifier.invalidmessage || "Invalid format");
                return;
            }
        }

        setValidationError(null);

        const payload = {
            customerId,
            identifierType: data.identifierType,
            identifier: data.identifier
        }
        console.log({ payload });
        props.apis
            .apiUnderWriterCKYCSearch(payload)
            .then((res) => {
                if (res) {
                    if (res?.status === "INVALID" || res?.status === "FAILED" || res?.error) {
                        const errorMsg = extractErrorMessage(res, "CKYC search failed");
                        swal2.fire({
                            title: errorMsg,
                            icon: "error",
                            iconColor: "#FF4D4D",
                        });
                        setCkycData(null);
                        setCkycDetails(null);
                        formRef.current?.form?.current?.reset?.();
                    } else {
                        setCkycData(res?.result?.[0] || null);
                        setCkycDetails(null);
                    }
                }
            })
            .catch((err) => {
                const errorMsg = extractErrorMessage(err, "CKYC search failed");
                swal2.fire({
                    title: errorMsg,
                    icon: "error",
                    iconColor: "#FF4D4D",
                });
                setCkycData(null);
                setCkycDetails(null);
            });

    };

    const handleCkycMobileSubmit = (e, data) => {
        e?.preventDefault?.();
        e?.stopPropagation?.();

        if (isRegenerating && !isAdmin) {
            swal2.fire({
                title: "Unauthorized Action",
                text: "Only Admin users have permission to regenerate CKYC reports.",
                icon: "error",
                iconColor: "#FF4D4D",
            });
            return;
        }

        const payload = {
            customerId,
            mobileNumber: data?.mobileNumber,
            ...(ckycData?.ckyc_ref_id ? { ckycReferenceId: ckycData.ckyc_ref_id } : {}),
            ...(isRegenerating ? { isRegenerate: true } : {}),
        };

        props.apis
            .apiUnderWriterDownloadCkycRequest(payload)
            .then((res) => {
                if (!res) {
                    setShowCkycOtpInput(false);
                    swal2.fire({
                        title: "Failed to generate CKYC report",
                        icon: "error",
                        iconColor: "#FF4D4D",
                    });
                    return;
                }

                // Check if report was generated directly (Legal Entity case with result_code 101)
                if (res?.result_code === 101 && res?.result) {
                    swal2.fire({
                        title: "CKYC Generated Successfully",
                        icon: "success",
                    });
                    setCkycDetails(res.result);
                    closeCkycModal();
                    return;
                }

                // Check for error responses from Digitap or backend
                const hasError =
                    res?.status === "INVALID" ||
                    res?.status === "FAILED" ||
                    res?.status === "FAILURE" ||
                    res?.error ||
                    (res?.http_response_code && res?.http_response_code !== 200) ||
                    (res?.statusCode && res?.statusCode >= 400);

                // Strict check for successful OTP generation
                const isSuccess =
                    !hasError &&
                    (res?.result_code === 103 || res?.http_response_code === 200 || res?.status === "SUCCESS") &&
                    Boolean(res?.ckyc_response_request_id || res?.request_id);

                if (isSuccess) {
                    setShowCkycOtpInput(true);
                    setResendTimer(30);
                } else {
                    setShowCkycOtpInput(false);
                    const errorMsg = extractErrorMessage(res, "Failed to generate CKYC report");
                    swal2.fire({
                        title: errorMsg,
                        icon: "error",
                        iconColor: "#FF4D4D",
                    });
                }
            })
            .catch((err) => {
                setShowCkycOtpInput(false);
                const errorMsg = extractErrorMessage(err, "Failed to generate CKYC report");
                swal2.fire({
                    title: errorMsg,
                    icon: "error",
                    iconColor: "#FF4D4D",
                });
            });
    };

    const handleGenerateCKYC = () => {
        setShowCkycModal(true);
        setShowCkycOtpInput(false);
        setCkycOtpValue("");
        setResendTimer(30);
    };

    const getUnderwriterInfo = () => {
        props.apis
            .apiGetUnderWriterApplicationDetails({
                applicationNumber: props.urlparams.params.id,
            })
            .then((res) => {
                if (res) {
                    setCustomerId(res?.customerId);
                    const ckyc = res?.customer?.customerCkyc;
                    if (ckyc && (ckyc.status === "VERIFIED" || ckyc.fullPayload)) {
                        setCustomerCkyc(ckyc);
                    } else if (ckyc && (ckyc.status === "INITIATED" || ckyc.status === "SEARCH_SAVED")) {
                        setCkycData({
                            name: ckyc.fullName,
                            ckyc_id: ckyc.ckycNo,
                            father_name: ckyc.fatherName,
                            ckyc_ref_id: ckyc.ckycReferenceId,
                            age: ckyc.age,
                            kyc_date: ckyc.kycDate,
                            mob_num: ckyc.mobileNumber,
                        });
                        setCustomerCkyc(null);
                    } else {
                        setCustomerCkyc(null);
                    }
                }
            });
    };

    useEffect(() => {
        getUnderwriterInfo();
    }, []);
    return (
        <>
            {!isAdmin && !customerCkyc &&
                <div className="card p-4">
                    <div className="flex_card_outer">
                        <div className="flex_card">
                            <h4 className="title">Search CKYC using any one identifier</h4>
                            <AppForm ref={formRef} onSubmit={handleSubmit} noValidate className="row">
                                <div className="col-12 col-md-4">
                                    <AppInput
                                        type="select"
                                        name="identifierType"
                                        label="Identifier Type"
                                        options={_enum.IDENTIFIER_TYPE}
                                        defaultValue={["PAN"]}
                                        onChange={handleChange}
                                        disabled={!!ckycDetails}
                                    />
                                </div>
                                <div className="col-12 col-md-4">
                                    <AppInput
                                        type="text"
                                        name="identifier"
                                        label={selectedIdentifier?.inputLabel || "Identifier Number"}
                                        errorMessage={validationError ? { name: "identifier", message: validationError } : null}
                                        onChange={() => setValidationError(null)}
                                        disabled={!!ckycDetails}
                                        required
                                    />
                                </div>
                                <div className="col-12 col-md-4 d-flex align-items-center mt-3">
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={!!ckycDetails}
                                    >
                                        <i className="fa fa-search me-2"></i>
                                        Search
                                    </button>
                                </div>
                            </AppForm>
                        </div>
                    </div>
                </div>
            }

            {(isAdmin && !customerCkyc && !isRegenerating) && <div className='card p-4 text-center'><h4 className='m-0'>No CKYC Data Found</h4></div>}

            {isRegenerating && ckycDetails ? (
                <>
                    <CkycCard
                        ckycData={{
                            name: ckycDetails.personal_details?.full_name || customerCkyc?.fullName || ckycData?.name,
                            ckyc_id: ckycDetails.personal_details?.ckyc_no || customerCkyc?.ckycNo || ckycData?.ckyc_id,
                            father_name: ckycDetails.personal_details?.father_fullname || customerCkyc?.fatherName || ckycData?.father_name,
                            ckyc_ref_id: ckycDetails.personal_details?.ckyc_reference_id || customerCkyc?.ckycReferenceId || ckycData?.ckyc_ref_id,
                            age: customerCkyc?.age || ckycData?.age,
                            kyc_date: ckycDetails.personal_details?.kyc_date || customerCkyc?.kycDate || ckycData?.kyc_date,
                            mobile_code: ckycDetails.personal_details?.mob_code,
                            mob_num: ckycDetails.personal_details?.mob_num || customerCkyc?.mobileNumber || ckycData?.mob_num,
                            remarks: ckycDetails.personal_details?.remarks || customerCkyc?.remarks || ckycData?.remarks,
                        }}
                        handleGenerateCKYC={handleGenerateCKYC}
                        isCkycGenerated={true}
                        hideGenerateButton={true}
                    />
                    <CkycDetailsCard details={ckycDetails} />
                    <div className="d-flex justify-content-end align-items-center mt-4">
                        <button
                            type="button"
                            className="btn btn-primary-lite me-3"
                            onClick={handleCancelRegenerate}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary me-3"
                            onClick={handleDownloadReport}
                        >
                            <i className="fa fa-download me-2"></i>
                            Download
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleSaveAndSubmit}
                        >
                            Save & Submit
                        </button>
                    </div>
                </>
            ) : customerCkyc ? (
                <>
                    <CkycCard
                        ckycData={{
                            name: customerCkyc.fullName || customerCkyc.fullPayload?.personal_details?.full_name,
                            ckyc_id: customerCkyc.ckycNo || customerCkyc.fullPayload?.personal_details?.ckyc_no,
                            father_name: customerCkyc.fatherName || customerCkyc.fullPayload?.personal_details?.father_fullname,
                            ckyc_ref_id: customerCkyc.ckycReferenceId || customerCkyc.fullPayload?.personal_details?.ckyc_reference_id,
                            age: customerCkyc.age,
                            kyc_date: customerCkyc.kycDate || customerCkyc.fullPayload?.personal_details?.kyc_date,
                            mobile_code: customerCkyc.fullPayload?.personal_details?.mob_code,
                            mob_num: customerCkyc.fullPayload?.personal_details?.mob_num || customerCkyc.mobileNumber,
                            remarks: customerCkyc.fullPayload?.personal_details?.remarks,
                        }}
                        handleGenerateCKYC={handleGenerateCKYC}
                        isCkycGenerated={true}
                        hideGenerateButton={true}
                        showRegenerateButton={isAdmin}
                        handleRegenerateCKYC={handleRegenerateCKYC}
                        isAdmin={isAdmin}
                    />
                    <CkycDetailsCard details={customerCkyc.fullPayload || customerCkyc} />
                    <div className="d-flex justify-content-end align-items-center mt-4">
                        <button
                            type="button"
                            className="btn btn-primary me-3"
                            onClick={handleDownloadReport}
                        >
                            <i className="fa fa-download me-2"></i>
                            Download
                        </button>
                        {isAdmin && (
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleRegenerateCKYC}
                            >
                                <i className="fa fa-refresh me-2 text-white"></i>
                                Regenerate CKYC
                            </button>
                        )}
                    </div>
                </>
            ) : (
                <>
                    {ckycData && <CkycCard ckycData={ckycData} handleGenerateCKYC={handleGenerateCKYC} isCkycGenerated={!!ckycDetails} />}
                    {ckycDetails && (
                        <>
                            <CkycDetailsCard details={ckycDetails} />
                            <div className="d-flex justify-content-end align-items-center mt-4">
                                <button
                                    type="button"
                                    className="btn btn-primary me-3"
                                    onClick={handleDownloadReport}
                                >
                                    <i className="fa fa-download me-2"></i>
                                    Download
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleSaveAndSubmit}
                                >
                                    Save & Submit
                                </button>
                            </div>
                        </>
                    )}
                </>
            )}
            {/* CKYC MODAL */}
            <Modal
                show={showCkycModal}
                onHide={closeCkycModal}
                centered
                backdrop="static"
                className="consent_otp_modal"
            >
                <Modal.Header
                    closeButton
                    className="border-0 py-4 d-flex align-item-start"
                >
                    <h2>CKYC Verification</h2>
                </Modal.Header>
                <Modal.Body className="pt-0">
                    <div className="row g-3 mb-4">
                        <AppForm onSubmit={handleCkycMobileSubmit}>
                            <AppInput
                                type="text"
                                name="mobileNumber"
                                label="Enter CKYC Registered Mobile No."
                                disabled={showCkycOtpInput}
                                placeholder="Enter 10 digit mobile number"
                                defaultValue={customerCkyc?.mobileNumber || customerCkyc?.fullPayload?.personal_details?.mob_num || ckycData?.mob_num || ""}
                                required
                            />
                            <div className={`col-12 d-flex align-items-center justify-content-end ${showCkycOtpInput ? "d-none" : ""}`}>
                                <button
                                    type="button"
                                    className="btn btn-primary-lite"
                                    onClick={closeCkycModal}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary ms-3"
                                >
                                    Generate
                                </button>
                            </div>
                        </AppForm>
                        {showCkycOtpInput && (
                            <>
                                <div className="col-12 d-flex flex-column">
                                    <div className="">
                                        <h3 className="">OTP to verify</h3>
                                        <p className="text-muted">We have sent an OTP to your mobile no.</p>
                                    </div>
                                    <div className="d-flex">
                                        <OTPInput
                                            value={ckycOtpValue}
                                            onChange={setCkycOtpValue}
                                            separator={<span>-</span>}
                                            renderInput={(props) => <input {...props} />}
                                            numInputs={6}
                                            shouldAutoFocus={true}
                                            isInputNum={true}
                                            containerStyle="otp_container small p-0"
                                        />
                                    </div>
                                    <div className="d-flex align-items-center justify-content-between mt-3">
                                        <span className="text-muted small">
                                            {resendTimer > 0 ? (
                                                <span>Resend OTP in <strong className="text-primary">{resendTimer}s</strong></span>
                                            ) : (
                                                <span>Didn't receive the OTP?</span>
                                            )}
                                        </span>
                                        <button
                                            type="button"
                                            className="btn btn-link p-0 text-decoration-none"
                                            onClick={handleResendOtp}
                                            disabled={resendTimer > 0 || isResending}
                                        >
                                            {isResending ? "Sending..." : "Resend OTP"}
                                        </button>
                                    </div>
                                </div>
                                <div className="col-12 d-flex align-items-center justify-content-end mt-4">
                                    <button
                                        type="button"
                                        className="btn btn-primary-lite"
                                        onClick={closeCkycModal}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary ms-3"
                                        onClick={handleCkycOtpVerify}
                                    >
                                        Verify
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </Modal.Body>
            </Modal>

            {!isAdmin && (!ckycDetails || customerCkyc) && (
                <div className="nav_container mt-5">
                    <Link
                        to={`/app/loandetails?activetab=${tabStatus}`}
                        className="btn btn-primary-lite me-3"
                    >
                        Cancel
                    </Link>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={(...args) =>
                            props.changeTab(...args, "backgroundverification")
                        }
                    >
                        Next
                    </button>
                </div>
            )}
        </>
    )
}

export default constore(CKYC)